/* eslint-disable react/jsx-props-no-spreading */
import {getPayload, type Payload} from 'payload';

import config from '@payload-config';
import {useLivePreview} from 'Application/useCases/useLivePreview';
import {SonosLayout} from 'UI/layouts/SonosLayout';
import {BlockRenderer} from 'UI/modules/BlockRenderer';

import type {GetStaticProps} from 'next';
import type {NextSSGPageWithLayout} from 'types/global';
import type {Page} from 'types/payload-types';

/**
 * The `ServerSideProps` interface defines the shape of the properties object for this page.
 * It outlines the required properties that needs to be provided when utilizing this page with server side rendering.
 * This interface is crucial for ensuring the correct usage of components or functions and for providing TypeScript type checking and IntelliSense support in IDEs.
 */
interface PageProps {
    pageData: Page;
}

/**
 * The `Home` component is the main landing page of the Sonos promotional application.
 * It serves as the root route and renders dynamic block-based content fetched from the Payload CMS.
 * The component integrates live preview functionality to allow real-time content editing in the CMS admin panel.
 *
 * @param props          The component props.
 * @param props.pageData The page data object fetched from the Payload CMS containing all block content and metadata for the landing page.
 * @returns A ReactNode representing the rendered block content of the landing page.
 *
 * @example
 * ```tsx
 * <Home pageData={pageData} />
 * ```
 */
const Home: NextSSGPageWithLayout<typeof getStaticProps> = ({pageData}) => {
    const page = useLivePreview<Page>({
        dataKey: 'Content',
        depth: 2,
        initialData: pageData
    });

    return (
        <BlockRenderer content={page.blocks} />
    );
};

/**
 * `getLayout` is a static property of the `Home` component that is a function designed to wrap the component with a specified layout.
 * It receives the router object, page properties, and the PageComponent and returns the PageComponent wrapped with the necessary layout and properties.
 * This property is essential for applying consistent layouts across different pages in the application.
 *
 * @param router        The Next.js router object representing the current route of the application.
 * @param pageProps     The page properties object representing the properties of the current page.
 * @param PageComponent The PageComponent representing the current page.
 * @returns A ReactNode representing the PageComponent wrapped with the necessary layout and properties.
 *
 * @example
 * ```tsx
 * const LayoutWrappedComponent = Home.getLayout(router, pageProps, PageComponent);
 * ```
 */
Home.getLayout = (router, pageProps, PageComponent) => (
    <SonosLayout
        description={pageProps.pageData.meta?.description}
        theme={pageProps.pageData.theme}
        title={pageProps.pageData.meta?.title}
    >
        <PageComponent router={router} {...pageProps} />
    </SonosLayout>
);

/**
 * `getLayoutKey` is a static property of the `Home` component that is a function designed to return the layout key string.
 * It is used to determine if the layout changed between pages to apply transitions between different layouts.
 * This property is crucial for managing different layouts within the application and for providing a specific layout key for the `Home` component.
 *
 * @returns A string representing the layout key.
 *
 * @example
 * ```tsx
 * const layoutKey = Home.getLayoutKey();
 * ```
 */
Home.getLayoutKey = () => '';

export default Home;

/**
 * `getServerSideProps` is a Next.js function designed to fetch data on each request and pass it as props to the page.
 * It is used in the context of Server-side Rendering and is crucial for pages that need to perform data fetching on every request, allowing for real-time data fetching and SEO optimization.
 * This function is essential for fetching the necessary data required by a page and providing it as props, ensuring the correct and up-to-date data is available during the rendering process.
 *
 * @param props                  The context object provided by Next.js containing various properties related to the request and response.
 * @param props.revalidateReason The reason for revalidation, indicating whether the page is being built or revalidated.
 * @returns A promise resolving to an object containing the `props` to be passed to the page component.
 */
export const getStaticProps: GetStaticProps<PageProps> = async ({revalidateReason}) => {
    let payload: Payload | undefined;

    if (revalidateReason !== 'build') {
        payload = await getPayload({config});
    }

    const pageData = await payload?.findGlobal({
        depth: 2,
        slug: 'page'
    }) ?? {
        blocks: [],
        id: 'id',
        livePreviewName: 'Content',
        meta: {},
        theme: 'light'
    };

    return {props: {pageData}};
};